import type { ReactNode } from 'react';

/**
 * The drawing primitives the engineering documents' diagrams are built from.
 *
 * Hand-placed SVG rather than a diagramming library, and the reasons are specific rather than
 * ideological. The shared Markdown renderer deliberately escapes raw HTML — KB bodies are written
 * from Slack messages, uploads and Granola notes, and that content is not ours to trust — so an
 * inline `<svg>` in a document would be shown as text. Rendering these as React components keeps
 * that property exactly intact: nothing is ever parsed out of a document, the markdown only names a
 * diagram and the component is looked up in a registry.
 *
 * Two more properties fall out of it and both are load-bearing here. Colour comes from the app's own
 * CSS variables, so a diagram is light or dark because the page is, with no second palette to keep
 * in step and no re-render on a theme change. And the layout is explicit, which is what these
 * particular pictures need: they are dense flows with annotations hanging off them, and every
 * auto-layout engine spends its effort on exactly the decisions that make them readable.
 *
 * Coordinates are user units on a 760-wide canvas, which is the documents' own column width — so at
 * full size one unit is one CSS pixel and a 12-unit label really is 12px.
 */

export type Tone = 'default' | 'muted' | 'accent' | 'info' | 'success' | 'danger';

/**
 * Fill, border and text per tone, straight from the app's tokens.
 *
 * `accent` is the warning ramp rather than a bespoke orange: it is the same family as the brand
 * colour, it already has a background and a ring tuned for both themes, and using it means a diagram
 * cannot drift from the console's palette.
 */
const TONES: Record<Tone, { fill: string; stroke: string; text: string }> = {
  default: { fill: 'var(--color-card)', stroke: 'var(--color-border-strong)', text: 'var(--color-ink)' },
  muted: { fill: 'var(--color-surface-muted)', stroke: 'var(--color-border)', text: 'var(--color-ink-secondary)' },
  accent: { fill: 'var(--color-warning-bg)', stroke: 'var(--color-warning-ring)', text: 'var(--color-warning-text)' },
  info: { fill: 'var(--color-info-bg)', stroke: 'var(--color-info-ring)', text: 'var(--color-info-text)' },
  success: { fill: 'var(--color-success-bg)', stroke: 'var(--color-success-ring)', text: 'var(--color-success-text)' },
  danger: { fill: 'var(--color-danger-bg)', stroke: 'var(--color-danger-ring)', text: 'var(--color-danger-text)' },
};

export const LINE = 'var(--color-border-strong)';
export const LINE_FAINT = 'var(--color-border)';
export const INK = 'var(--color-ink)';
export const INK_MUTED = 'var(--color-ink-muted)';
export const INK_FAINT = 'var(--color-ink-faint)';
export const MONO = 'ui-monospace, SFMono-Regular, Menlo, monospace';

export type Point = readonly [number, number];

/**
 * One diagram: a scroll container, the canvas, and a caption.
 *
 * The SVG scales to the column and then stops. Below `minWidth` the figure scrolls sideways instead
 * of shrinking further, because these carry 10px labels and a diagram scaled to a phone's width is
 * not a smaller diagram, it is an unreadable one — the same rule the house style already applies to
 * wide tables and code blocks.
 *
 * `role="img"` with a `<title>` is what a screen reader gets, since none of the text inside an SVG
 * reads as a sentence; the caption is for everyone and carries the one line the picture is making.
 */
export function Figure({
  title,
  caption,
  width,
  height,
  minWidth,
  children,
}: {
  /** The accessible name. Read instead of the shapes, so it states what the diagram shows. */
  title: string;
  caption?: ReactNode;
  width: number;
  height: number;
  minWidth?: number;
  children: ReactNode;
}) {
  return (
    <figure className="my-6">
      <div className="overflow-x-auto rounded-lg border border-border bg-surface-subtle px-4 py-4">
        <svg
          role="img"
          viewBox={`0 0 ${width} ${height}`}
          width="100%"
          style={{ height: 'auto', minWidth: minWidth ?? Math.min(width, 560), display: 'block' }}
        >
          <title>{title}</title>
          {children}
        </svg>
      </div>
      {caption && (
        <figcaption className="mt-2 text-[11px] leading-relaxed text-ink-faint">{caption}</figcaption>
      )}
    </figure>
  );
}

/**
 * A labelled box.
 *
 * `sub` is the second line — a tool count, a column name, a queue — and it is deliberately dimmer
 * than the title rather than smaller only: at these sizes weight separates two lines better than
 * size does.
 */
export function Node({
  x,
  y,
  w = 150,
  h = 44,
  title,
  sub,
  tone = 'default',
  mono = false,
  dashed = false,
}: {
  x: number;
  y: number;
  w?: number;
  h?: number;
  title: string;
  sub?: string;
  tone?: Tone;
  /** Renders the title in the monospace face — for anything that is an identifier rather than a name. */
  mono?: boolean;
  dashed?: boolean;
}) {
  const t = TONES[tone];
  const cx = x + w / 2;
  return (
    <g>
      <rect
        x={x}
        y={y}
        width={w}
        height={h}
        rx={6}
        fill={t.fill}
        stroke={t.stroke}
        strokeWidth={1}
        {...(dashed ? { strokeDasharray: '4 3' } : {})}
      />
      {sub ? (
        <>
          <text
            x={cx}
            y={y + h / 2 - 2}
            textAnchor="middle"
            fontSize={11.5}
            fontWeight={600}
            fill={t.text}
            {...(mono ? { fontFamily: MONO } : {})}
          >
            {title}
          </text>
          <text x={cx} y={y + h / 2 + 12} textAnchor="middle" fontSize={9.5} fill={t.text} opacity={0.72}>
            {sub}
          </text>
        </>
      ) : (
        <text
          x={cx}
          y={y + h / 2 + 4}
          textAnchor="middle"
          fontSize={11.5}
          fontWeight={600}
          fill={t.text}
          {...(mono ? { fontFamily: MONO } : {})}
        >
          {title}
        </text>
      )}
    </g>
  );
}

/**
 * A decision point, drawn as a rounded lozenge rather than the classic diamond.
 *
 * A diamond wastes most of its area on corners and forces the label outside it at this scale; the
 * lozenge reads as "a question" from its shape alone and still holds four words.
 */
export function Gate({ x, y, w = 150, h = 40, title, sub }: { x: number; y: number; w?: number; h?: number; title: string; sub?: string }) {
  const cx = x + w / 2;
  return (
    <g>
      <rect x={x} y={y} width={w} height={h} rx={h / 2} fill="var(--color-surface)" stroke={LINE} strokeWidth={1} />
      <text x={cx} y={sub ? y + h / 2 - 2 : y + h / 2 + 4} textAnchor="middle" fontSize={11} fontWeight={600} fill={INK}>
        {title}
      </text>
      {sub && (
        <text x={cx} y={y + h / 2 + 11} textAnchor="middle" fontSize={9} fill={INK_MUTED}>
          {sub}
        </text>
      )}
    </g>
  );
}

/**
 * A polyline with an arrowhead, routed through explicit points.
 *
 * The head is drawn inline rather than as a `<marker>`, which matters for a reason that only shows
 * up with more than one diagram on a page: marker ids live in the *document*, so two figures each
 * defining `#arrow` would have the second silently take the first's definition. Computing the
 * triangle from the final segment costs three lines and cannot collide.
 */
export function Arrow({
  points,
  dashed = false,
  color = LINE,
  label,
  labelAt,
  labelAnchor = 'middle',
  head = true,
}: {
  points: Point[];
  dashed?: boolean;
  color?: string;
  label?: string;
  /** Where the label sits. Defaults to just above the midpoint of the first segment. */
  labelAt?: Point;
  labelAnchor?: 'start' | 'middle' | 'end';
  head?: boolean;
}) {
  const d = points.map(([x, y], i) => `${i === 0 ? 'M' : 'L'} ${x} ${y}`).join(' ');
  const [px, py] = points[points.length - 2] ?? points[0]!;
  const [qx, qy] = points[points.length - 1]!;
  const angle = Math.atan2(qy - py, qx - px);
  const size = 5.5;
  const spread = Math.PI / 7;
  const triangle = [
    [qx, qy],
    [qx - size * Math.cos(angle - spread), qy - size * Math.sin(angle - spread)],
    [qx - size * Math.cos(angle + spread), qy - size * Math.sin(angle + spread)],
  ]
    .map(([x, y]) => `${x},${y}`)
    .join(' ');

  const [lx, ly] = labelAt ?? [(points[0]![0] + points[1]![0]) / 2, (points[0]![1] + points[1]![1]) / 2 - 6];

  return (
    <g>
      <path
        d={d}
        fill="none"
        stroke={color}
        strokeWidth={1.25}
        strokeLinejoin="round"
        {...(dashed ? { strokeDasharray: '4 3' } : {})}
      />
      {head && <polygon points={triangle} fill={color} />}
      {label && (
        <text x={lx} y={ly} textAnchor={labelAnchor} fontSize={9.5} fill={INK_MUTED}>
          {label}
        </text>
      )}
    </g>
  );
}

/** A dashed region grouping several nodes, with its name in the corner. */
export function Lane({
  x,
  y,
  w,
  h,
  label,
  tone = 'muted',
}: {
  x: number;
  y: number;
  w: number;
  h: number;
  label?: string;
  tone?: Tone;
}) {
  const t = TONES[tone];
  return (
    <g>
      <rect x={x} y={y} width={w} height={h} rx={8} fill="none" stroke={t.stroke} strokeWidth={1} strokeDasharray="5 4" />
      {label && (
        <text x={x + 10} y={y + 14} fontSize={9} fontWeight={700} fill={INK_FAINT} letterSpacing="0.06em">
          {label.toUpperCase()}
        </text>
      )}
    </g>
  );
}

/** Free text. `lines` stack downward from `y` at the given leading. */
export function Note({
  x,
  y,
  lines,
  anchor = 'start',
  size = 9.5,
  color = INK_MUTED,
  leading = 12,
  mono = false,
  weight,
}: {
  x: number;
  y: number;
  lines: string | string[];
  anchor?: 'start' | 'middle' | 'end';
  size?: number;
  color?: string;
  leading?: number;
  mono?: boolean;
  weight?: number;
}) {
  const rows = Array.isArray(lines) ? lines : [lines];
  return (
    <g>
      {rows.map((line, i) => (
        <text
          key={i}
          x={x}
          y={y + i * leading}
          textAnchor={anchor}
          fontSize={size}
          fill={color}
          {...(mono ? { fontFamily: MONO } : {})}
          {...(weight ? { fontWeight: weight } : {})}
        >
          {line}
        </text>
      ))}
    </g>
  );
}

/** A small rounded tag — a queue name, a column, a status. */
export function Pill({
  x,
  y,
  label,
  tone = 'muted',
  mono = true,
}: {
  x: number;
  y: number;
  label: string;
  tone?: Tone;
  mono?: boolean;
}) {
  const t = TONES[tone];
  const w = label.length * (mono ? 5.6 : 5.9) + 14;
  return (
    <g>
      <rect x={x} y={y} width={w} height={17} rx={8.5} fill={t.fill} stroke={t.stroke} strokeWidth={1} />
      <text
        x={x + w / 2}
        y={y + 12}
        textAnchor="middle"
        fontSize={9}
        fontWeight={600}
        fill={t.text}
        {...(mono ? { fontFamily: MONO } : {})}
      >
        {label}
      </text>
    </g>
  );
}

/** A horizontal rule inside a canvas, for separating bands of a diagram. */
export function Divider({ x, y, w, dashed = true }: { x: number; y: number; w: number; dashed?: boolean }) {
  return (
    <line
      x1={x}
      y1={y}
      x2={x + w}
      y2={y}
      stroke={LINE_FAINT}
      strokeWidth={1}
      {...(dashed ? { strokeDasharray: '3 4' } : {})}
    />
  );
}
